import { useState, type CSSProperties } from "react";
import { RunRoutineModal } from "./RunRoutineModal";
import type { Routine } from "../../../lib/types";

interface RunNowButtonProps {
  routine: Routine;
  /** "row" = compact button for list rows; "primary" = filled CTA for the detail view. */
  variant?: "row" | "primary";
}

/**
 * Sprint 26.1 — "Run now" trigger. Owns the {@link RunRoutineModal} so the
 * federated run flow (compile → surface procedure → record outcome) is reusable
 * from both the Routines tab rows and the RoutineView detail header.
 */
export function RunNowButton({ routine, variant = "row" }: RunNowButtonProps) {
  const [open, setOpen] = useState(false);

  function openRun(e: React.MouseEvent) {
    e.stopPropagation();
    setOpen(true);
  }

  return (
    <>
      <button
        type="button"
        onClick={openRun}
        style={variant === "primary" ? primaryBtn : rowBtn}
        title="Run this routine"
        data-testid="run-now-button"
        {...(variant === "primary" ? { "data-variant": "primary" } : {})}
      >
        <i className="ti ti-player-play" style={{ fontSize: 12 }} aria-hidden="true" /> Run now
      </button>
      <RunRoutineModal open={open} onClose={() => setOpen(false)} routine={routine} />
    </>
  );
}

const rowBtn: CSSProperties = {
  fontSize: 12,
  padding: "4px 8px",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  flexShrink: 0,
  alignSelf: "flex-start",
};

const primaryBtn: CSSProperties = {
  fontSize: 13,
  padding: "8px 14px",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
};
